// The Worklist and History (spec §3): one page component, two modes. History is read-only.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import type { RowSelectionState, SortingState } from "@tanstack/react-table";
import {
  CalendarClockIcon,
  CheckCheckIcon,
  CircleCheckIcon,
  CopyIcon,
  FilterXIcon,
  FlagIcon,
  PencilLineIcon,
  TruckIcon,
  XIcon,
} from "lucide-react";
import {
  PAGES,
  approvedText,
  lineOrderKey,
  lineWords,
  toApprove,
  toSchedule,
  preRouteOfLine,
  DEFAULT_KINDS,
  ROUTING_STATUS_LIST,
  startingStatusRecords,
  DEFAULT_WAREHOUSES,
  EXCEL_SOURCE_LABEL,
  HISTORY_TABS,
  WAREHOUSE_GROUPS,
  WORKLIST_TABS,
  appointmentNotScheduled,
  isImportedLine,
  importedChipShown,
  appointmentPlan,
  countText,
  divisionFilterText,
  isMyLine,
  keyOfCustomer,
  openingViewOf,
  viewParams,
  viewToAddress,
  withoutView,
  formatShortDay,
  scheduledText,
  customerFilterOptions,
  customerNamer,
  defaultRange,
  defaultView,
  differsFromDefault,
  historyOrder,
  inTab,
  inWarehouses,
  lineIdOf,
  kindOf,
  listUsage,
  localToday,
  NO_HOLIDAYS,
  pageRows,
  priorityOrder,
  pushStep,
  routeTodayOrLate,
  routingRows,
  savedWarehouses,
  scopeLineIds,
  toSearch,
  undoableStep,
  viewChips,
  deposcoChecked,
  myCustomersChip,
  PE_FIELDS,
  shownCustomers,
  clockText,
} from "@app/shared";
import type {
  DateRange,
  Filters,
  GridView,
  ViewParams,
  LineView,
  AnyListName,
  Mode,
  MyViewPayload,
  Row,
  Scope,
  SourceFilter,
  TabId,
  WarehouseGroup,
} from "@app/shared";
import { Button, DataError, RegisteredPage, TableSkeleton, ToolbarButton, useMe } from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { useColumnOrder } from "./column-drag";
import { PINNABLE, orderColumns, pinColumns, pinTick, unpinnedOf, upgradeHidden } from "./column-order";
import type { HiddenStep } from "./column-order";
import { COLUMNS, HISTORY_HIDDEN, OPEN_COLUMNS, pageColumns } from "./columns";
import type { ColumnDef } from "./columns";
import {
  routingKey,
  useHolidays,
  useListsConfig,
  useMyView,
  usePreRoutes,
  useRoutingData,
  useSourceVersion,
} from "./data";
import { AddValueDialog, FinishDialog, ManageListDialog } from "./dialogs";
import { PreRouteMarks } from "./chips";
import { RoutingGrid } from "./grid";
import type { PageSearch } from "../../pages";
import { parseSort, readSort, writeSort } from "./saved-sort";
import { hasDefault } from "./my-default";
import { MyCustomersChip } from "./my-customers";
import { MessageProvider, notify, useMessage } from "./message";
import { LineDetails } from "./line-details";
import { useShipFlow } from "./ship-flow";
import {
  ColorKey,
  ColumnsMenu,
  Counters,
  DateRangePicker,
  FilterPill,
  SearchBox,
  StatusTabs,
  WarehousePicker,
} from "./toolbar";
import type { AppliedStep } from "./ask-ai-bar";
import { AskAiBar } from "./ask-ai-bar";
import { useAskAi } from "./axai";
import { DeposcoNotLines, SourcePicker, useSourceFilter } from "./source-filter";
import { useWriter } from "./writer";
import { SelectionBar } from "./selection-bar";
import { SetValueDialog } from "./set-value-dialog";
import { runFill } from "./fill-run";

/** A per-browser convenience (the warehouse, hidden columns): never state that must survive. */
function stored<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function store(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private window: kept for this page only.
  }
}

/**
 * This browser tab's last view of the page (`openingViewOf`), in its address form: a new tab or
 * window starts clean, so filters never come back days later. Unreadable: as if none.
 */
const TAB_VIEW = (mode: Mode) => `routing.view.${mode}`;
function tabView(mode: Mode): unknown {
  try {
    const raw = sessionStorage.getItem(TAB_VIEW(mode));
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}
function keepTabView(mode: Mode, address: Record<string, string | string[]>) {
  try {
    if (Object.keys(address).length) sessionStorage.setItem(TAB_VIEW(mode), JSON.stringify(address));
    else sessionStorage.removeItem(TAB_VIEW(mode));
  } catch {
    // Not kept: the page's tab opens it clean.
  }
}

const ALL_COLUMNS = [...COLUMNS, ...OPEN_COLUMNS];
const DEFAULT_HIDDEN = ALL_COLUMNS.filter((c) => c.hidden).map((c) => c.id);
const DEPOSCO_ONLY = ALL_COLUMNS.filter((c) => c.deposco).map((c) => c.id);
const A2000_ONLY = ALL_COLUMNS.filter((c) => c.a2000).map((c) => c.id);
/**
 * Each Source view keeps its own hidden columns: All the defaults; A2000 without Deposco's columns;
 * Deposco with its own (DC, DLIX, CO, Group) shown and A2000's (WMS #, Shipped in A2000) hidden.
 */
const DEFAULT_HIDDEN_OF: Record<SourceFilter, string[]> = {
  all: DEFAULT_HIDDEN,
  A2000: [...new Set([...DEFAULT_HIDDEN, ...DEPOSCO_ONLY])],
  Deposco: [...DEFAULT_HIDDEN.filter((id) => !DEPOSCO_ONLY.includes(id)), ...A2000_ONLY],
};
/** A page's default hidden columns of a Source view: History hides Routing window too (`HISTORY_HIDDEN`). */
const defaultHiddenOf = (mode: Mode, source: SourceFilter) =>
  mode === "history" ? [...new Set([...DEFAULT_HIDDEN_OF[source], ...HISTORY_HIDDEN])] : DEFAULT_HIDDEN_OF[source];
const hiddenKey = (mode: Mode, source: SourceFilter) =>
  source === "all" ? `routing.hidden.${mode}` : `routing.hidden.${mode}.${source.toLowerCase()}`;
/**
 * The defaults a saved column choice predates (`upgradeHidden`): version 2 (V7 step 1) shows WH and
 * hides the new DC; version 3 (step 2) hides the new Mixed column (its chip is on the WMS #); version 4
 * the new Tab; version 5 the new DLIX, CO and Group (Deposco); version 6 the ten PE Order columns
 * (017), in every Source view. A new default-hidden column is a new step here.
 */
const HIDDEN_STEPS: readonly HiddenStep[] = [
  { version: 2, hide: ["dc"], show: ["wh"] },
  { version: 3, hide: ["mixed"] },
  { version: 4, hide: ["stateTab"] },
  { version: 5, hide: ["dlix", "co", "group"] },
  { version: 6, hide: PE_FIELDS },
];
/** Version 7: History's Routing window unpinned and hidden (always "—" there); the Worklist's unchanged. */
const stepsOf = (mode: Mode): HiddenStep[] => [
  ...HIDDEN_STEPS,
  { version: 7, hide: mode === "history" ? HISTORY_HIDDEN : [] },
];
const HIDDEN_VERSION = 7;

/** Someone's own hidden columns of a Source view, brought up to the current defaults once. */
function storedHidden(mode: Mode, source: SourceFilter): string[] {
  const key = hiddenKey(mode, source);
  const saved = stored<string[] | null>(key, null);
  if (!saved) return defaultHiddenOf(mode, source);
  const version = stored(`${key}.version`, 1);
  if (version >= HIDDEN_VERSION) return saved;
  const next = upgradeHidden(saved, version, stepsOf(mode));
  store(key, next);
  store(`${key}.version`, HIDDEN_VERSION);
  return next;
}
const SOURCE_VIEWS: SourceFilter[] = ["all", "A2000", "Deposco"];

/**
 * `line`: open on that line (the System log's Go to line): selected, in view, its details open.
 * The person's saved default and customers are read first, so the first view is already theirs;
 * unreadable, the page opens as it does for everyone.
 */
/** "2:05 PM", or "—" with no time. */
const clock = (iso: string | null | undefined) => (iso ? clockText(new Date(iso)) : "—");

/**
 * Deposco loads only the orders that changed: "checked" is its job's last run, "last change" the
 * newest change among the B2B orders the page reads. With no last run known, the last change alone.
 */
function DeposcoTimes({ checkedAt: read, lastChange }: { checkedAt: string | null; lastChange: string | null }) {
  // The version and the page are read apart: checked is never shown before the page's last change.
  const checkedAt = read ? deposcoChecked(read, lastChange) : null;
  return (
    <span
      className="flex items-center gap-1"
      title={
        checkedAt
          ? "Checked: when Deposco last loaded. Last change: the last time a B2B order on this page changed."
          : undefined
      }
      data-testid="deposco-times"
    >
      <span className="size-1.5 rounded-full bg-sky-500" />
      {checkedAt
        ? `Deposco · checked ${clock(checkedAt)} · last change ${clock(lastChange)}`
        : `Deposco · ${clock(lastChange)}`}
    </span>
  );
}

export function RoutingPage({ mode, line, view }: { mode: Mode; line?: string; view?: ViewParams }) {
  const my = useMyView();
  return (
    <RegisteredPage id={mode}>
      <MessageProvider>
        {my.isPending && !line ? (
          <TableSkeleton />
        ) : (
          <RoutingScreen mode={mode} focus={line} urlView={view} my={my.data ?? null} />
        )}
      </MessageProvider>
    </RegisteredPage>
  );
}

function RoutingScreen({
  mode,
  focus,
  urlView,
  my,
}: {
  mode: Mode;
  focus?: string;
  urlView?: ViewParams;
  my: MyViewPayload | null;
}) {
  const today = useMemo(() => localToday(), []);
  const holidays = useHolidays();
  const me = useMe();
  const message = useMessage();
  const who = me.data?.authenticated ? me.data.user.displayName || me.data.user.username : "";

  // The reader's own warehouses, as they chose them by hand.
  const [home] = useState<WarehouseGroup[]>(() =>
    savedWarehouses(stored<unknown[]>("routing.warehouses", DEFAULT_WAREHOUSES)),
  );
  // A2000, Deposco or both: the person's own, kept for every table (`source-filter.tsx`).
  const [rememberedSource, rememberSource] = useSourceFilter();
  // The page's starting view: the reader's own warehouses and Source. Over it, once, the first that
  // exists of the address's view (a link, Back), the one this browser tab last left here (the page's
  // tab), the person's saved default, their customers (`openingViewOf`); written as it changes.
  const base: GridView = { ...defaultView(mode, today), warehouses: home, source: rememberedSource };
  const myKeys = my?.customers.keys ?? [];
  const hasMine = myKeys.length > 0;
  // My customers as the filter: by key, and a workbook row (a name only) by its spelling.
  const myFilter = { keys: myKeys, spellings: my?.customers.spellings ?? [] };
  const saved = my?.preferences[mode];
  const savedView = (() => {
    try {
      return saved?.view ? (JSON.parse(saved.view) as unknown) : undefined;
    } catch {
      return undefined;
    }
  })();
  const [start] = useState(() =>
    focus
      ? null
      : openingViewOf(mode, { address: urlView, remembered: tabView(mode), saved: savedView, hasMine }, base, today),
  );
  // My customers (`assignments.ts`): the lines of the person's customers, until they take it off.
  const [mine, setMine] = useState(start?.mine ?? false);
  // Its chip: on, or "Show my customers" to put it back; none on Go to line or for someone with none.
  const myChip = focus ? null : myCustomersChip(hasMine, mine);
  // Opened on one line (Go to line): every warehouse and any date, so it is on the page — for now
  // only: a chip says so, Clear filters puts the reader's own back, and nothing of it is saved.
  const [warehouses, setWarehouses] = useState<WarehouseGroup[]>(() => (focus ? [] : (start?.view.warehouses ?? home)));
  const [fromGoTo, setFromGoTo] = useState(!!focus);
  const [source, setSource] = useState<SourceFilter>(() => (focus ? "all" : (start?.view.source ?? rememberedSource)));
  const [search, setSearch] = useState(start?.view.search ?? "");
  const [customers, setCustomers] = useState<string[]>(start?.view.customers ?? []);
  const [statuses, setStatuses] = useState<string[]>(start?.view.statuses ?? []);
  // History: orders only by default (app lines count as orders); the workbook's duplicates hidden.
  const [kinds, setKinds] = useState<string[]>(() => start?.view.kinds ?? (mode === "history" ? DEFAULT_KINDS : []));
  const [duplicates, setDuplicates] = useState(start?.view.duplicates ?? false);
  const [range, setRange] = useState<DateRange>(
    () =>
      start?.view.range ??
      (focus ? { ...defaultRange(mode, today), preset: "any", from: null, to: null } : defaultRange(mode, today)),
  );
  // History reads only the lines that finished on its days; a range on another date reads them all.
  const days = mode === "history" && range.field === "finishedAt" ? { from: range.from, to: range.to } : {};
  const data = useRoutingData(mode, days);
  const writer = useWriter(routingKey(mode, days), who, today);
  // The pre-routes (the shell's read, shared): each line's "Pre-routed" marker.
  const preRoutes = usePreRoutes();
  // Deposco's last run moves without a reload (no B2B order changed): read from the version, not the page.
  const version = useSourceVersion();
  const preRouteMarks = useMemo(() => preRouteOfLine(preRoutes.data?.preRoutes ?? []), [preRoutes.data]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(start?.view.columns ?? {});
  const [tab, setTab] = useState<TabId>(start?.view.tab ?? "all");
  const [unscheduledOnly, setUnscheduledOnly] = useState(start?.notScheduled ?? false);
  // "Imported from the workbook": the lines Admin → Import made (`isImportedLine`), from its Result's link.
  // Only while the server's `ROUTING_IMPORT` is on: off, no chip, and `v.imp` keeps nothing.
  const importOn = version.data?.importOn === true;
  const [importedChosen, setImportedOnly] = useState(start?.imported ?? false);
  // A manager's "Unassigned customers": open lines holding a customer no Owner has (`assignments.ts`).
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const owned = useMemo(() => (my?.ownedKeys ? new Set(my.ownedKeys) : null), [my?.ownedKeys]);
  const unassigned = (l: LineView) => !!owned && l.customers.some((c) => c.code && !owned.has(keyOfCustomer(c)));
  // The person's last sort on this page, back when they open or refresh it (`saved-sort.ts`).
  // A saved default's sort wins over this browser's (`my-default.tsx`).
  const [sorting, setSortingState] = useState<SortingState>(() =>
    hasDefault(saved) ? parseSort(saved?.sort) : readSort(mode),
  );
  const setSorting = (s: SortingState) => {
    setSortingState(s);
    writeSort(mode, s);
  };
  const [selection, setSelection] = useState<RowSelectionState>({});
  const [orderReset, setOrderReset] = useState(0);
  // The selection bar's Set value… (`set-value-dialog.tsx`).
  const [settingValue, setSettingValue] = useState(false);
  const [details, setDetails] = useState<string | null>(focus ?? null);
  const [hiddenOf, setHiddenOf] = useState<Record<SourceFilter, string[]>>(
    () => Object.fromEntries(SOURCE_VIEWS.map((v) => [v, storedHidden(mode, v)])) as Record<SourceFilter, string[]>,
  );
  const hidden = hiddenOf[source];
  const setHidden = (h: string[]) => {
    setHiddenOf((all) => ({ ...all, [source]: h }));
    store(hiddenKey(mode, source), h);
    store(`${hiddenKey(mode, source)}.version`, HIDDEN_VERSION);
  };
  const [rowColors, setRowColors] = useState<boolean>(() => stored("routing.rowColors", true));
  // Routing window unpinned by this person (the Columns menu's "Pinned"), kept with their column set.
  const [unpinned, setUnpinnedState] = useState<string[]>(() => unpinnedOf(stored(`routing.unpinned.${mode}`, [])));
  const setUnpinned = (next: string[]) => {
    setUnpinnedState(next);
    store(`routing.unpinned.${mode}`, next);
  };
  const [finishing, setFinishing] = useState<LineView[] | null>(null);
  const [adding, setAdding] = useState<{ column: ColumnDef; typed: string; done: (v: string | null) => void } | null>(
    null,
  );
  const [managing, setManaging] = useState<ColumnDef | null>(null);

  const payload = data.data;
  const manager = payload?.manager ?? false;
  // A Viewer's page reads only: no editor, no write button (`canWrite`, the server's `routingWrite`).
  const canWrite = payload?.canWrite ?? false;
  // History: the app's lines and the old workbook's rows, side by side — never merged. Windows count
  // the server's holidays (the reports' too); unreadable, weekends only, and the page says so.
  const calendar = holidays.data?.calendar ?? NO_HOLIDAYS;
  const { assembled, rows: allRows } = useMemo(
    () => (payload ? routingRows(payload, today, calendar) : { assembled: { lines: [] }, rows: [] }),
    [payload, today, calendar],
  );

  const columns = useMemo(
    () =>
      // History: how the line finished in Review's place (its review is in the line's details), and
      // Routing window unpinned there (`pageColumns`).
      pageColumns(mode).filter((c) => manager || !c.managerOnly),
    [mode, manager],
  );
  const pinnedColumns = useMemo(() => pinColumns(columns, unpinned), [columns, unpinned]);
  // Put in order in the Columns menu, kept per person (`column-drag.tsx`).
  const columnOrder = useColumnOrder(`routing.order.${mode}`);
  const orderedColumns = useMemo(
    () => orderColumns(pinnedColumns, columnOrder.order),
    [pinnedColumns, columnOrder.order],
  );
  const visibleColumns = useMemo(
    () => orderedColumns.filter((c) => c.sticky || !hidden.includes(c.id)),
    [orderedColumns, hidden],
  );
  const columnValue = (row: Row, id: string) => {
    const c = columns.find((x) => x.id === id);
    return c?.values ? [...c.values(row).map((v) => v.value), ...(c.matchAlso?.(row) ?? [])] : (c?.value(row) ?? "");
  };

  const filters: Filters = {
    source,
    warehouses,
    customers,
    statuses,
    range,
    search,
    columns: columnFilters,
    kinds,
    duplicates,
    mine: mine ? myFilter : undefined,
  };
  const onPage = pageRows(allRows, mode, filters, columnValue);
  const tabs = (mode === "worklist" ? WORKLIST_TABS : HISTORY_TABS).map((t) => t.id);
  const counts = Object.fromEntries(tabs.map((t) => [t, onPage.filter((r) => inTab(r, t)).length]));
  // The Source filter's counts: what the other filters leave in each source.
  const bothSources = pageRows(allRows, mode, { ...filters, source: "all" }, columnValue);
  const sourceCounts = {
    A2000: bothSources.filter((r) => r.line.source === "A2000").length,
    Deposco: bothSources.filter((r) => r.line.source === "Deposco").length,
  };
  // "Appointment not scheduled" (item 8): lines with a date stored before but on no pickup, to clean up.
  const notScheduled = onPage.filter((r) => appointmentNotScheduled(r.line));
  const imported = onPage.filter((r) => isImportedLine(r.line));
  // Shown only with lines to show or a run made (`importedChipShown`); hidden, it keeps nothing.
  const showImported = mode === "worklist" && importedChipShown(importOn, imported.length, payload?.importRuns ?? null);
  const importedOnly = importedChosen && showImported;
  const rows = (unscheduledOnly ? notScheduled : onPage)
    .filter((r) => !importedOnly || isImportedLine(r.line))
    .filter((r) => !unassignedOnly || unassigned(r.line))
    .filter((r) => inTab(r, tab))
    .sort(mode === "worklist" ? priorityOrder : historyOrder);

  // The filter pills count what the other filters leave.
  const scope = pageRows(allRows, mode, { ...filters, customers: [], statuses: [], kinds: [] }, columnValue);
  // A Mixed line counts once under each of its values (customers).
  const countBy = (pick: (r: Row) => string | readonly string[]) => {
    const m = new Map<string, number>();
    for (const r of scope) {
      const v = pick(r);
      for (const one of typeof v === "string" ? [v] : v) m.set(one, (m.get(one) ?? 0) + 1);
    }
    return [...m]
      .filter(([v]) => v)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, count]) => ({ value, label: value, count }));
  };
  // The Customer filter lists each shown name once (`customerFilterOptions`): customers sharing one
  // are one option, which keeps every line of any of them; its hover lists their names and codes.
  // Ask AI and a saved view may keep an original name: the filter matches both (`keepsCustomer`).
  const customerOptions = customerFilterOptions(scope.map((r) => r.line));

  const storedLines = assembled.lines.filter((l) => l.stored);
  const usageOf = (list: AnyListName | undefined) =>
    list && list !== "Carrier"
      ? listUsage(storedLines, columns.find((c) => c.list === list)!.field!)
      : new Map<string, number>();
  const lists = useListsConfig(manager && managing !== null);
  const listValues = payload?.listValues ?? [];
  // Routing status starts as the seven (`routing-status.ts`): its first change writes them, with their categories.
  const startingOf = (list: AnyListName) =>
    list === ROUTING_STATUS_LIST ? startingStatusRecords() : list === "Carrier" ? [] : (lists.data?.[list] ?? []);
  const seedStatuses = async () => {
    if (!listValues.some((v) => v.list === ROUTING_STATUS_LIST))
      await writer.loadStartingValues(ROUTING_STATUS_LIST, startingStatusRecords(), listValues);
  };

  // A ticked line stays ticked across its first save (Set value, a fill down): its key goes from
  // `auto:…` to the stored id, its group key stays (`lineOrderKey`).
  const groupOfKey = useRef(new Map<string, string>());
  useEffect(() => {
    const keys = new Map(assembled.lines.map((l) => [lineOrderKey(l), l.key]));
    const now = new Set(assembled.lines.map((l) => l.key));
    const moved = Object.keys(selection).filter((k) => selection[k] && !now.has(k));
    const carried = moved.flatMap((k) => {
      const to = keys.get(groupOfKey.current.get(k) ?? "");
      return to ? [[k, to] as const] : [];
    });
    if (carried.length)
      setSelection((s) => {
        const next = { ...s };
        for (const [from, to] of carried) {
          delete next[from];
          next[to] = true;
        }
        return next;
      });
    groupOfKey.current = new Map(assembled.lines.map((l) => [l.key, lineOrderKey(l)]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- on each new read of the lines only
  }, [assembled]);
  const selectedRows = rows.filter((r) => selection[r.line.key]);
  const selectedLines = selectedRows.map((r) => r.line);
  const selectedApprove = toApprove(selectedLines);
  const allScheduled = selectedLines.length > 0 && toSchedule(selectedLines).todo.length === 0;
  /** Approve the selected lines not approved yet; when some are, the orange "Are you sure?" names them first. */
  const approveSelected = async () => {
    const { done, todo } = toApprove(selectedLines);
    if (!todo.length) return;
    if (done.length) {
      const ok = await message.confirm(
        "Some lines are already approved — are you sure?",
        `${countText(done.length, "line")} ${done.length === 1 ? "is" : "are"} approved already and stay${done.length === 1 ? "s" : ""} as ${done.length === 1 ? "it is" : "they are"}. Only the others are approved.`,
        {
          lines: [
            ...done.map((l) => `Already approved: ${approvedText(l)}`),
            ...todo.map((l) => `Will be approved: ${lineWords(l)}`),
          ],
          confirmLabel: `Approve the ${countText(todo.length, "other")}`,
        },
      );
      if (!ok) return;
    }
    await writer.approve(todo);
    clearSelection();
  };
  const clearSelection = () => setSelection({});
  // Go to line names the stored line; the grid's rows are the lines' keys. Once it is there, it is selected.
  // Every line the page has, History's workbook rows too (their key: `xl-<row>`).
  const lineOf = (id: string) => allRows.find((r) => r.line.key === id || r.line.stored?.id === id)?.line;
  const focusKey = focus ? lineOf(focus)?.key : undefined;
  const [focusSelected, setFocusSelected] = useState(false);
  if (focusKey && !focusSelected) {
    setFocusSelected(true);
    setSelection({ [focusKey]: true });
  }
  // Go to line: the line's details, while it is on the page.
  const detailsLine = details ? lineOf(details) : undefined;
  // Its routing window in words: the pinned cell says it short.
  const detailsWindow = detailsLine ? allRows.find((r) => r.line.key === detailsLine.key)?.window : undefined;
  // Schedule pickup and Mark shipped (the action bar and the Ship column).
  const flow = useShipFlow({ writer, payload, lines: assembled.lines, today, onDone: clearSelection });

  /**
   * An Appointment date entered on the Worklist (item 8): it is the pickup's date. On no pickup,
   * Schedule pickup with that date (Cancel saves nothing; Ctrl+Z takes them off again); on a pickup,
   * Reschedule to it; cleared, off the pickup once the person says so — a date stored before, just
   * forgotten.
   */
  const onAppointment = async (lines: LineView[], date: string, done: () => void) => {
    const plan = appointmentPlan(lines, date);
    // Every dialog a date cell opens gives the focus back to the grid when it closes (`done`).
    if (plan.kind === "refuse") return message.error(plan.title, plan.why).then(done);
    if (plan.kind === "schedule") return flow.schedule(plan.lines, { date: plan.date, undoable: true, onClosed: done });
    if (plan.kind === "reschedule")
      return flow.reschedule(
        flow.rows.filter((r) => plan.lines.some((l) => l.onPickup?.row.id === r.row.id)),
        plan.date,
        done,
      );
    if (plan.kind !== "clear") return done();
    if (plan.takeOff.length) {
      const days = [...new Set(plan.takeOff.map((l) => l.onPickup!.pickup.pickupDate ?? ""))].filter(Boolean);
      const ok = await message.confirm(
        `Take ${plan.takeOff.length === 1 ? "it" : "them"} off the pickup${days.length === 1 ? ` on ${formatShortDay(days[0]!)}` : "s"}?`,
        "The line goes back on the Worklist with no scheduled ship date, as Remove on the Shipping page.",
        { lines: plan.takeOff.map((l) => scheduledText(l)), confirmLabel: "Take off the pickup" },
      );
      done();
      if (ok) await flow.shipping.takeOff(plan.takeOff, (id) => flow.rows.filter((r) => r.pickup.id === id));
    } else done();
    for (const l of plan.forget) await writer.saveField(l, "appointmentDate", "");
  };

  const openRows = allRows.filter((r) => !r.line.finished && inWarehouses(r.line.wh, warehouses));
  // "N to route now or late": Past deadline + Route now, as the tabs count them; Ask AI says the same.
  const late = onPage.filter(routeTodayOrLate).length;
  const older = range.from ? openRows.filter((r) => r.line.ptDate && r.line.ptDate < range.from!).length : 0;
  const gridRef = useRef<HTMLDivElement>(null);
  // The picker's counts: this page's lines (open, or finished) in each warehouse, other filters aside.
  const pageLines = allRows.filter((r) => (mode === "worklist" ? !r.line.finished : r.line.finished));
  // The customers of the open lines no Owner has, each once: a manager's chip on the Worklist.
  const unassignedCount =
    mode === "worklist" && owned
      ? new Set(
          pageLines.flatMap((r) =>
            r.line.customers.filter((c) => c.code && !owned.has(keyOfCustomer(c))).map(keyOfCustomer),
          ),
        ).size
      : 0;
  const warehouseCounts = Object.fromEntries(
    WAREHOUSE_GROUPS.map((w) => [w, pageLines.filter((r) => r.line.whs.includes(w)).length]),
  ) as Record<WarehouseGroup, number>;

  // The workbook's duplicates the other filters leave: what "Show duplicates (N)" would add.
  const duplicateCount =
    mode === "history"
      ? pageRows(allRows, mode, { ...filters, duplicates: true }, columnValue).filter((r) => r.line.excel?.duplicate)
          .length
      : 0;
  const fromExcel = rows.filter((r) => r.line.excel).length;

  const units = rows.reduce((n, r) => n + r.line.qty, 0);
  const pts = rows.reduce((n, r) => n + r.line.pts.length, 0);
  // Only open pick tickets have a value: no total where no row has one (History).
  const dollars =
    manager && rows.some((r) => r.line.value !== null) ? rows.reduce((n, r) => n + (r.line.value ?? 0), 0) : null;

  // Ask AI (the template's chat widget, axai.ts): the grid's state as one view.
  const gridView: GridView = {
    source,
    warehouses,
    customers,
    statuses,
    kinds,
    duplicates,
    range,
    columns: columnFilters,
    tab,
    sorting,
    search,
  };
  // The view the grid keeps its order under (`stable-order.ts`): a row stays where the person saw it
  // while this is the same — any filter, the tab, the Source, the warehouses or the sort changing, or
  // Clear filters (`orderReset`, even when the view was already the default), shows the order afresh.
  const orderKey = JSON.stringify([mode, gridView, unscheduledOnly, unassignedOnly, mine, orderReset]);
  // The filters in the address, replaced as they change (no history entry each), and kept for this
  // browser tab: Back to this page, or its tab, brings them back; Clear filters (the starting view)
  // empties both. The sort stays out: it is the person's, kept in this browser.
  const navigate = useNavigate();
  const inAddress = focus
    ? undefined
    : viewParams(gridView, base, unscheduledOnly, hasMine ? mine : undefined, importedOnly);
  const addressKey = JSON.stringify(inAddress ?? null);
  useEffect(() => {
    const address = viewToAddress(inAddress);
    if (!focus) keepTabView(mode, address);
    void navigate({
      to: ".",
      search: ((prev: PageSearch) => ({ ...withoutView(prev), ...address })) as never,
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the key is the view
  }, [addressKey]);
  // Opened with the view kept from earlier: said beside Clear filters until the view changes.
  const [keptKey] = useState(() => (start?.kept ? addressKey : null));

  // Only a choice by hand is kept as the reader's warehouses: never one Ask AI set, or its Undo.
  const applyGridView = (v: GridView, byHand = false) => {
    setSource(v.source);
    if (byHand) rememberSource(v.source);
    setWarehouses(v.warehouses);
    if (byHand) store("routing.warehouses", v.warehouses);
    setCustomers(v.customers);
    setStatuses(v.statuses);
    setKinds(v.kinds);
    setDuplicates(v.duplicates);
    setRange(v.range);
    setColumnFilters(v.columns);
    setTab(v.tab);
    setSorting(v.sorting);
    setSearch(v.search);
  };

  // The view as one key, with what the search box and hidden columns change, which the report's
  // terms cannot say: Ask AI's answer and Undo last while it is unchanged.
  const versionOf = (v: GridView) => JSON.stringify([toSearch(mode, v), JSON.stringify([v.search, v.columns, hidden])]);
  // The lines a view shows, per tab and on its tab, counted exactly as the grid counts them.
  const shownFor = (v: GridView) => {
    const on = pageRows(allRows, mode, v, columnValue);
    return {
      shown: on.filter((r) => inTab(r, v.tab)).length,
      counts: Object.fromEntries(tabs.map((t) => [t, on.filter((r) => inTab(r, t)).length])),
    };
  };
  // The customers this page has, whatever the filters: what Ask AI may set the picker to — by original
  // name or by shown name (Admin → Customers): the Customer filter keeps either (`keepsCustomer`).
  const pageCustomers = [
    ...new Set(pageLines.flatMap((r) => [...r.line.customerNames, ...shownCustomers(r.line)]).filter(Boolean)),
  ];
  // Their divisions by abbreviation, likewise.
  const pageDivisions: Record<string, string[]> = {};
  for (const r of pageLines)
    for (const d of r.line.divs)
      if (d.abbrev && !pageDivisions[d.abbrev]?.includes(d.name)) (pageDivisions[d.abbrev] ??= []).push(d.name);
  // And their codes, so Ask AI may name a customer as the grid shows it.
  const pageCodes = Object.fromEntries(
    pageLines.flatMap((r) => r.line.customers.filter((c) => c.code).map((c) => [c.code, c.name])),
  );
  // Ask AI's steps, last on top: Undo puts back the view before the top one while the grid is still
  // the view it set — then the one before it may be undone too. A bar question may set the grid
  // more than once; that is one step, back to before the question.
  const [aiSteps, setAiSteps] = useState<AppliedStep[]>([]);
  const turnBefore = useRef<GridView | null>(null);
  const aiStep = undoableStep(aiSteps, versionOf(gridView));
  const headerOf = (id: string) => columns.find((c) => c.id === id)?.header ?? id;
  const pageDivs = allRows.flatMap((r) => r.line.divs);
  const chips = viewChips(mode, gridView, today, headerOf, (id, values) =>
    id === "division" ? divisionFilterText(values, pageDivs) : values.join(", "),
  );
  // Go to line's every-warehouse view, until the reader changes the warehouses.
  const goToChip = fromGoTo && warehouses.length === 0;
  // The reader's own warehouses are their default here: Clear filters goes back to them.
  const ownWarehouses = warehouses.length === home.length && warehouses.every((w) => home.includes(w));
  const filtered = differsFromDefault(
    mode,
    ownWarehouses ? { ...gridView, warehouses: defaultView(mode, today).warehouses } : gridView,
    today,
  );
  useAskAi({
    mode,
    today,
    view: gridView,
    apply: (next, unapplied) => {
      const question = turnBefore.current;
      setAiSteps((steps) =>
        pushStep(steps, { before: question ?? gridView, version: versionOf(next), unapplied }, question),
      );
      applyGridView(next);
    },
    // Within My customers too, as the grid shows it.
    // Within My customers while it is on, as the grid shows it — "without the view" too.
    lineIdsFor: (scope: Scope) =>
      scopeLineIds(mine ? allRows.filter((r) => isMyLine(r.line, myFilter)) : allRows, mode, scope, columnValue),
    allLineIds: () =>
      (mine ? pageLines.filter((r) => isMyLine(r.line, myFilter)) : pageLines).map((r) => lineIdOf(r.line)),
    everyLineId: () => pageLines.map((r) => lineIdOf(r.line)),
    headerOf,
    shownFor,
    counts,
    shown: rows.length,
    customers: pageCustomers,
    customerCodes: pageCodes,
    divisionNames: pageDivisions,
    noWms: allRows.filter((r) => r.line.noWms && !r.line.finished).length,
    myCustomers: {
      on: mine,
      labels: my?.customers.labels ?? [],
      codes: myKeys.map((k) => k.slice(k.indexOf(":") + 1)),
    },
    sourceCounts,
    deposcoWaiting: payload?.deposcoWaiting?.length ?? 0,
    manager,
  });

  if (data.isPending || holidays.isPending) return <TableSkeleton />;
  if (data.isError) return <DataError error={data.error} />;

  const finish = async () => {
    const notGone = selectedLines.filter((l) => !l.allGone);
    if (notGone.length) {
      return message.error(
        "Only lines gone from A2000 or Deposco can be finished here",
        "Finish… is for lines whose pick tickets (SOs) are all gone from A2000 (Deposco). To cancel an open line, set its Routing status to Cancelled; for a line that left, use Mark shipped.",
      );
    }
    setFinishing(selectedLines);
  };

  return (
    <div className="routing-page space-y-3">
      {holidays.isError || holidays.data?.degraded ? (
        <p className="text-muted-foreground text-xs" data-testid="routing-holidays-degraded">
          {holidays.isError
            ? "Holidays not loaded: routing dates count weekends only."
            : "Jewish holidays not loaded: routing dates count weekends and US holidays only."}
        </p>
      ) : null}
      <AskAiBar
        mode={mode}
        version={versionOf(gridView)}
        step={aiStep}
        onBegin={() => {
          turnBefore.current = gridView;
        }}
        onEnd={() => {
          turnBefore.current = null;
        }}
        onUndo={() => {
          if (!aiStep) return;
          applyGridView(aiStep.before);
          setAiSteps((steps) => steps.slice(0, -1));
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        <SourcePicker
          value={source}
          counts={sourceCounts}
          onChange={(v) => {
            setSource(v);
            rememberSource(v);
          }}
        />
        <WarehousePicker
          value={warehouses}
          counts={warehouseCounts}
          onChange={(w) => {
            setWarehouses(w);
            setFromGoTo(false);
            store("routing.warehouses", w);
          }}
        />
        <SearchBox value={search} onChange={setSearch} />
        <FilterPill label="Customer" options={customerOptions} selected={customers} onChange={setCustomers} />
        <FilterPill
          label="Routing status"
          options={countBy((r) => r.line.fields.routingStatus)}
          selected={statuses}
          onChange={setStatuses}
        />
        {mode === "history" ? (
          <FilterPill label="Kind" options={countBy((r) => kindOf(r.line))} selected={kinds} onChange={setKinds} />
        ) : null}
        <DateRangePicker mode={mode} value={range} today={today} onChange={setRange} />
        {mode === "history" && (duplicateCount > 0 || duplicates) ? (
          <ToolbarButton
            icon={<CopyIcon />}
            label={duplicates ? "Hide duplicates" : "Show duplicates"}
            // The count as a badge: a phone shows the icon and the badge, not the words.
            badge={duplicateCount}
            hint="Rows the old workbook had twice — hidden unless shown here"
            variant={duplicates ? "secondary" : "outline"}
            aria-pressed={duplicates}
            onClick={() => setDuplicates((d) => !d)}
          />
        ) : null}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* My default (my-default.tsx) is off the toolbar: each table keeps its sort and filters by itself
              now. A default saved before still opens the page (`openingViewOf`). */}
          <ColumnsMenu
            columns={orderedColumns}
            order={columnOrder}
            hidden={hidden}
            onChange={setHidden}
            onReset={() => {
              columnOrder.reset();
              setHidden(defaultHiddenOf(mode, source));
              setUnpinned([]);
            }}
            // Routing window is pinned on the Worklist only: History has it unpinned and hidden.
            pinnable={mode === "worklist" ? PINNABLE : []}
            unpinned={unpinned}
            onPin={(id, pinned) => setUnpinned(pinTick(unpinned, id, pinned))}
            rowColors={rowColors}
            onRowColors={(on) => {
              setRowColors(on);
              store("routing.rowColors", on);
            }}
          />
          <ColorKey mode={mode} />
        </div>
      </div>

      {chips.length || goToChip || myChip ? (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="routing-filter-chips">
          {/* Off: "Show my customers" puts it back, Unassigned customers off with it (an Owner's
              customers are never unassigned). */}
          <MyCustomersChip
            hasMine={hasMine && !focus}
            mine={mine}
            count={myKeys.length}
            labels={my?.customers.labels ?? []}
            note={
              mode === "history"
                ? "The old workbook's rows, which have a name only, count when the name is one of theirs."
                : ""
            }
            onChange={(on) => {
              setMine(on);
              if (on) setUnassignedOnly(false);
            }}
          />
          {goToChip ? (
            <span className="bg-muted inline-flex max-w-full items-center gap-1 rounded-full border py-0.5 pr-1 pl-2.5 text-xs">
              <span className="truncate">All warehouses (Go to line)</span>
              <button
                type="button"
                aria-label="Back to your warehouses"
                className="hover:bg-background rounded-full p-0.5"
                onClick={() => {
                  setWarehouses(home);
                  setFromGoTo(false);
                }}
              >
                <XIcon className="size-3" aria-hidden />
              </button>
            </span>
          ) : null}
          {chips.map((c) => (
            <span
              key={c.id}
              className="bg-muted inline-flex max-w-full items-center gap-1 rounded-full border py-0.5 pr-1 pl-2.5 text-xs"
            >
              <span className="truncate">{c.text}</span>
              <button
                type="button"
                aria-label={`Remove ${c.text}`}
                className="hover:bg-background rounded-full p-0.5"
                onClick={() => applyGridView(c.without)}
              >
                <XIcon className="size-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <StatusTabs
        mode={mode}
        tab={tab}
        counts={counts}
        source={source}
        onChange={setTab}
        noRuleLink={
          payload?.manager
            ? () =>
                void navigate({
                  to: PAGES.admin.path as never,
                  search: { admin: "routing-rules", rules: "none" } as never,
                })
            : undefined
        }
      />
      {/* The line above the table: the summary on the left, Clear filters at the table's right edge. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        {mode === "worklist" ? (
          <Counters
            today={today}
            late={late}
            toReview={counts.review ?? 0}
            older={older}
            onReview={() => setTab("review")}
            onOlder={() => setRange({ ...range, preset: "any", from: null, to: null })}
          >
            {source !== "A2000" && payload?.deposcoWaiting?.length ? (
              <DeposcoNotLines rows={payload.deposcoWaiting} name={customerNamer(payload.customerNames)} />
            ) : null}
            {notScheduled.length || unscheduledOnly ? (
              <>
                {source !== "A2000" && payload?.deposcoWaiting?.length ? (
                  <span className="text-muted-foreground"> · </span>
                ) : null}
                <button
                  type="button"
                  aria-pressed={unscheduledOnly}
                  title="Lines with an appointment date entered before, on no pickup: enter the date again to schedule"
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-xs font-medium",
                    unscheduledOnly ? "bg-foreground text-background border-foreground" : "text-primary hover:bg-muted",
                  )}
                  onClick={() => setUnscheduledOnly((v) => !v)}
                  data-testid="appointment-not-scheduled"
                >
                  Appointment not scheduled ({notScheduled.length})
                </button>
              </>
            ) : null}
            {showImported ? (
              <>
                <span className="text-muted-foreground"> · </span>
                <button
                  type="button"
                  aria-pressed={importedOnly}
                  title="Lines Admin → Import made from the PRODUCTION CHECK OFF workbook (grouped by “Workbook import”)"
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-xs font-medium",
                    importedOnly ? "bg-foreground text-background border-foreground" : "text-primary hover:bg-muted",
                  )}
                  onClick={() => setImportedOnly((v) => !v)}
                  data-testid="imported-from-workbook"
                >
                  Imported from the workbook ({imported.length})
                </button>
              </>
            ) : null}
            {unassignedCount || unassignedOnly ? (
              <>
                <span className="text-muted-foreground"> · </span>
                <button
                  type="button"
                  aria-pressed={unassignedOnly}
                  title="Open lines whose customer has no Owner (Admin → Customer assignments)"
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-xs font-medium",
                    unassignedOnly ? "bg-foreground text-background border-foreground" : "text-primary hover:bg-muted",
                  )}
                  onClick={() => {
                    // My customers have Owners: both on would show nothing.
                    if (!unassignedOnly) setMine(false);
                    setUnassignedOnly((v) => !v);
                  }}
                  data-testid="unassigned-customers"
                >
                  Unassigned customers ({unassignedCount})
                </button>
              </>
            ) : null}
          </Counters>
        ) : (
          <p className="text-muted-foreground text-sm">
            Shipped and cancelled lines · nothing deleted · with the old workbook's "Shipped Orders 2026" rows (
            {EXCEL_SOURCE_LABEL}, read-only).
          </p>
        )}
        {keptKey === addressKey ? (
          <span
            className="text-muted-foreground ml-auto text-xs"
            title="This page opened with the filters you left it with: some lines may be hidden"
            data-testid="routing-filters-kept"
          >
            Filters kept from earlier
          </span>
        ) : null}
        {filtered || unscheduledOnly || importedOnly || unassignedOnly || mine ? (
          <Button
            variant="outline"
            size="xs"
            className={cn("gap-1", keptKey === addressKey ? undefined : "ml-auto")}
            // The reader's own warehouses back; Clear filters never saves a warehouse choice.
            onClick={() => {
              setOrderReset((n) => n + 1);
              applyGridView({ ...defaultView(mode, today), warehouses: home, sorting });
              // Clear filters shows both sources again, and keeps that.
              rememberSource("all");
              setFromGoTo(false);
              setUnscheduledOnly(false);
              setImportedOnly(false);
              setUnassignedOnly(false);
              // The app's default: no customers, My customers too.
              setMine(false);
            }}
            data-testid="routing-clear-filters"
          >
            <FilterXIcon className="size-3.5" />
            Clear filters
          </Button>
        ) : null}
      </div>

      {mode === "worklist" ? (
        <SelectionBar
          active={selectedLines.length > 0}
          tone="dark"
          hint={
            canWrite
              ? "Tick lines to approve, schedule a pickup or mark shipped several at once"
              : "Tick lines to count them or export them"
          }
        >
          <span className="font-medium">
            {countText(selectedLines.length, "line")} selected ·{" "}
            {countText(
              selectedLines.reduce((n, l) => n + l.pts.length, 0),
              "pick ticket",
            )}{" "}
            ·{" "}
            {countText(
              selectedLines.reduce((n, l) => n + l.qty, 0),
              "unit",
            )}
          </span>
          <div className="ml-auto flex flex-wrap gap-1.5">
            {/* Every selected line approved already: no Approve (item 4). A Viewer: none of the writes. */}
            {canWrite && selectedApprove.todo.length ? (
              <ToolbarButton
                icon={<CheckCheckIcon />}
                label={`Approve ${selectedApprove.todo.length}`}
                hint="Approve the selected New, Updated and Split lines"
                variant="secondary"
                onClick={() => void approveSelected()}
              />
            ) : null}
            {canWrite ? (
              <>
                <ToolbarButton
                  icon={<CalendarClockIcon />}
                  label="Schedule pickup"
                  hint={
                    allScheduled
                      ? "Already scheduled"
                      : "Put the selected lines on a pickup: they go on the Shipping schedule"
                  }
                  variant="secondary"
                  // Off, but still hovered for its reason: focusable, aria-disabled.
                  disabled={allScheduled}
                  focusableWhenDisabled
                  className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                  onClick={() => void flow.schedule(selectedLines)}
                />
                <ToolbarButton
                  icon={<TruckIcon />}
                  label="Mark shipped"
                  hint="The selected lines left: they go to the Ship log and History"
                  variant="secondary"
                  onClick={() => void flow.markShipped(selectedLines)}
                />
                <ToolbarButton
                  icon={<PencilLineIcon />}
                  label="Set value…"
                  hint="One field, one value, on every selected line — as if typed in each cell"
                  variant="secondary"
                  onClick={() => setSettingValue(true)}
                  data-testid="set-value"
                />
              </>
            ) : null}
            {canWrite && selectedLines.some((l) => l.allGone) ? (
              <ToolbarButton
                icon={<FlagIcon />}
                label="Finish…"
                hint="Shipped or cancelled — for lines gone from A2000"
                variant="secondary"
                onClick={() => void finish()}
              />
            ) : null}
            <ToolbarButton
              icon={<XIcon />}
              label="Clear"
              hint="Clear the selection"
              variant="secondary"
              onClick={clearSelection}
            />
          </div>
        </SelectionBar>
      ) : null}

      <PreRouteMarks.Provider value={preRouteMarks}>
        <RoutingGrid
          mode={mode}
          rows={rows}
          columns={visibleColumns}
          today={today}
          manager={manager}
          canWrite={canWrite}
          rowColors={rowColors}
          writer={writer}
          allLines={assembled.lines}
          listValues={payload?.listValues ?? []}
          usage={(c) => usageOf(c.list)}
          selection={selection}
          onSelectionChange={setSelection}
          sorting={sorting}
          onSortingChange={setSorting}
          columnFilters={columnFilters}
          onColumnFilter={(id, vals) => setColumnFilters((f) => ({ ...f, [id]: vals }))}
          onAddValue={(column, typed, done) => setAdding({ column, typed, done })}
          onManageList={(c) => setManaging(c)}
          headerRows={(id) => {
            // Every filter but this column's own: its other values stay listed.
            const { [id]: _own, ...others } = filters.columns;
            return pageRows(allRows, mode, { ...filters, columns: others }, columnValue)
              .filter((r) => !unscheduledOnly || appointmentNotScheduled(r.line))
              .filter((r) => !importedOnly || isImportedLine(r.line))
              .filter((r) => !unassignedOnly || unassigned(r.line))
              .filter((r) => inTab(r, tab));
          }}
          onAppointment={mode === "worklist" ? (ls, d, done) => void onAppointment(ls, d, done) : undefined}
          onShip={canWrite ? (line) => void flow.markShipped([line]) : undefined}
          // Details (the row's pinned right part, or Enter on its first cell): the line's details; the address stays.
          onDetails={(line) => setDetails(line.stored?.id ?? line.key)}
          focus={focusKey}
          orderKey={orderKey}
          gridRef={gridRef}
          height="max(26rem, calc(100dvh - 22rem))"
        />
      </PreRouteMarks.Provider>

      <footer className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span>
          {countText(rows.length, "line")}
          {mode === "history"
            ? ` (${(rows.length - fromExcel).toLocaleString("en-US")} App + ${fromExcel.toLocaleString("en-US")} ${EXCEL_SOURCE_LABEL})`
            : ""}{" "}
          · {mode === "history" ? countText(pts, "App pick ticket") : countText(pts, "pick ticket")} ·{" "}
          {countText(units, "unit")}
          {dollars !== null ? ` · $${Math.round(dollars).toLocaleString("en-US")}` : ""}
        </span>
        <span className="flex items-center gap-1" title="A2000 reloads every open pick ticket each run: its last load.">
          <span className="size-1.5 rounded-full bg-emerald-500" />
          A2000 · {clock(payload?.sourceLoadedAt)}
        </span>
        <DeposcoTimes
          checkedAt={version.data?.deposcoCheckedAt ?? payload?.deposcoCheckedAt ?? null}
          lastChange={payload?.deposcoLoadedAt ?? null}
        />
        <span className={cn("ml-auto flex items-center gap-1", writer.saving > 0 && "text-foreground")}>
          {writer.saving > 0 ? (
            "Saving…"
          ) : (
            <>
              <CircleCheckIcon className="size-3.5 text-emerald-600" />
              {writer.lastSavedAt
                ? `All changes saved · ${clockText(writer.lastSavedAt)} · everyone sees them`
                : "Up to date"}
            </>
          )}
        </span>
      </footer>

      {flow.dialogs}
      {detailsLine ? (
        <LineDetails line={detailsLine} routingWindow={detailsWindow} onClose={() => setDetails(null)} />
      ) : null}
      {finishing ? (
        <FinishDialog
          lines={finishing}
          onClose={() => setFinishing(null)}
          onFinish={(status, note) => {
            const ls = finishing;
            setFinishing(null);
            void writer.finish(ls, status, note).then(clearSelection);
          }}
        />
      ) : null}
      {settingValue && selectedRows.length ? (
        <SetValueDialog
          rows={selectedRows}
          columns={orderedColumns}
          mode={mode}
          today={today}
          listValues={payload?.listValues ?? []}
          usage={(c) => usageOf(c.list)}
          manager={manager}
          onAddValue={(column, typed, done) => setAdding({ column, typed, done })}
          onClose={() => setSettingValue(false)}
          onApply={(column, plan) =>
            void runFill({
              def: column,
              plan,
              kind: "set",
              writer,
              message,
              onAppointment: (ls, d, done) => void onAppointment(ls, d, done),
            })
          }
        />
      ) : null}
      {adding?.column.list ? (
        <AddValueDialog
          list={adding.column.list}
          header={adding.column.header}
          typed={adding.typed}
          values={payload?.listValues ?? []}
          manager={manager}
          onClose={() => {
            adding.done(null);
            setAdding(null);
          }}
          onUse={(v) => {
            notify("That value is already on the list — used it");
            adding.done(v);
            setAdding(null);
          }}
          onAdd={(v) => {
            const a = adding;
            setAdding(null);
            void writer.addListValue(a.column.list!, v).then((rec) => {
              if (rec) notify(`Added “${rec.value}” to ${a.column.header}`);
              a.done(rec ? rec.value : null);
            });
          }}
        />
      ) : null}
      {managing?.list && manager ? (
        <ManageListDialog
          list={managing.list}
          header={managing.header}
          values={payload?.listValues ?? []}
          usage={usageOf(managing.list)}
          starting={startingOf(managing.list).map((v) => (typeof v === "string" ? v : v.value))}
          onClose={() => setManaging(null)}
          onAdd={async (v, category) => {
            if (managing.list === ROUTING_STATUS_LIST) await seedStatuses();
            const rec = await writer.addListValue(managing.list!, v, category);
            if (rec) notify(`Added “${rec.value}” to ${managing.header}`);
            return rec !== undefined;
          }}
          onCategory={(rec, category) => void writer.setListCategory(rec, category)}
          onRename={(rec, v) => void writer.renameListValue(rec, v, assembled.lines)}
          onRetire={(rec, r) => void writer.retireListValue(rec, r)}
          onDelete={(rec) => {
            const used = usageOf(rec.list).get(rec.value) ?? 0;
            if (used > 0) {
              void message.error(
                "Can't delete this value",
                `“${rec.value}” is used on ${used} line(s). Edit it, retire it, or merge it into another value instead.`,
              );
            } else void writer.deleteListValue(rec);
          }}
          onMerge={(keep, others) => void writer.mergeListValues(keep, others, assembled.lines)}
          onLoadStarting={() => void writer.loadStartingValues(managing.list!, startingOf(managing.list!), listValues)}
        />
      ) : null}
    </div>
  );
}
